import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { PageType } from 'api/page-template/types';
import {
  MeetingTemplateSection,
  PagesAPI,
  Document,
  SaveDocumentPayload,
} from 'api/pages';
import { CompanyPage, EmailPage, MeetingPage, Page } from 'api/pages/types';

/** Use string id so cache key matches whether documentId comes as number or string (e.g. from route params) */
const DOCUMENT_QUERY_KEY = (documentId: number | string) =>
  ['document', String(documentId)] as const;

const DOCUMENT_VERSION_HISTORY_QUERY_KEY = (documentId: number) =>
  ['document', documentId, 'version-history'] as const;

export const useGetDocumentVersionHistoryQuery = (
  documentId: number,
  options?: Omit<
    UseQueryOptions<
      unknown[],
      Error,
      unknown[],
      ReturnType<typeof DOCUMENT_VERSION_HISTORY_QUERY_KEY>
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryKey: DOCUMENT_VERSION_HISTORY_QUERY_KEY(documentId),
    queryFn: () => PagesAPI.getDocumentVersionHistory(documentId),
    enabled: Boolean(documentId),
    ...options,
  });
};

export const useGetDocumentQuery = (
  meetingId: number,
  options?: Omit<
    UseQueryOptions<
      Document,
      Error,
      Document,
      ReturnType<typeof DOCUMENT_QUERY_KEY>
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryKey: DOCUMENT_QUERY_KEY(meetingId),
    queryFn: () => PagesAPI.getDocument(meetingId),
    ...options,
  });
};

const getPagesQueryKey = (type: PageType) => ['pages', type] as const;

export const useGetMeetingPagesQuery = (
  options?: Omit<
    UseQueryOptions<Page[], Error, Page[], ReturnType<typeof getPagesQueryKey>>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryKey: getPagesQueryKey(PageType.Meeting),
    queryFn: () => PagesAPI.getPages(PageType.Meeting),
    ...options,
  });
};
const DefaultTemplateSectionsQueryKey = (pageType: PageType) =>
  ['default-template-sections', pageType] as const;

export const useGetTemplateSectionsQuery = (
  pageType: PageType,
  options?: Omit<
    UseQueryOptions<
      MeetingTemplateSection[],
      Error,
      MeetingTemplateSection[],
      ReturnType<typeof DefaultTemplateSectionsQueryKey>
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryKey: DefaultTemplateSectionsQueryKey(pageType),
    queryFn: () => PagesAPI.getDefaultTemplateSections(pageType),
    ...options,
  });
};
const getPagesQueryKeyForCompany = (type: PageType, companyId: number) =>
  [...getPagesQueryKey(type), companyId] as const;
const useGetPagesForCompanyQuery = <TypeT extends PageType, PageT extends Page>(
  type: TypeT,
  companyId: number,
  options?: Omit<
    UseQueryOptions<
      PageT[],
      Error,
      PageT[],
      ReturnType<typeof getPagesQueryKeyForCompany>
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryKey: getPagesQueryKeyForCompany(type, companyId),
    queryFn: () => PagesAPI.getPages<TypeT, PageT>(type, companyId),
    enabled: companyId !== undefined,
    ...options,
  });
};

export const useGetMeetingPagesForCompanyQuery = (
  companyId: number,
  options?: Omit<
    UseQueryOptions<
      MeetingPage[],
      Error,
      MeetingPage[],
      ReturnType<typeof getPagesQueryKeyForCompany>
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  return useGetPagesForCompanyQuery(PageType.Meeting, companyId, options);
};

export const useGetEmailPagesForCompanyQuery = (
  companyId: number,
  options?: Omit<
    UseQueryOptions<
      EmailPage[],
      Error,
      EmailPage[],
      ReturnType<typeof getPagesQueryKeyForCompany>
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryKey: getPagesQueryKeyForCompany(PageType.Email, companyId),
    queryFn: () => PagesAPI.getGroupedPages(PageType.Email, companyId),
    enabled: companyId !== undefined,
    ...options,
  });
};

/**
 * Fetches email pages from scarlet /v1/pages?pageType=EMAIL&companyId=...
 * (same pattern as meeting pages). Transform flat Page[] to EmailPage[] for existing UI.
 * Revert to useGetEmailPagesForCompanyQuery (get-pages-grouped) if needed.
 */
const getEmailPagesFromPagesQueryKey = (companyId: number) =>
  [
    ...getPagesQueryKeyForCompany(PageType.Email, companyId),
    'scarlet',
  ] as const;

export const useGetEmailPagesFromPagesQuery = (
  companyId: number,
  options?: Omit<
    UseQueryOptions<
      Page[],
      Error,
      Page[],
      ReturnType<typeof getEmailPagesFromPagesQueryKey>
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryKey: getEmailPagesFromPagesQueryKey(companyId),
    queryFn: () => PagesAPI.getPages(PageType.Email, companyId),
    enabled: companyId !== undefined,
    ...options,
  });
};

export const useGetCompanyPagesForCompanyQuery = (
  companyId: number,
  options?: Omit<
    UseQueryOptions<
      CompanyPage[],
      Error,
      CompanyPage[],
      ReturnType<typeof getPagesQueryKeyForCompany>
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  return useGetPagesForCompanyQuery(PageType.Company, companyId, options);
};

const getPagesByTypeListQueryKey = (
  sourceId: number,
  pageTypeList: string,
  objectType: 'COMPANY' | 'PROJECT',
) => ['pages', 'multiple', sourceId, pageTypeList, objectType] as const;

export const useGetPagesByTypeListQuery = (
  sourceId: number,
  pageTypeList: string,
  objectType: 'COMPANY' | 'PROJECT' = 'COMPANY',
  options?: Omit<
    UseQueryOptions<
      Page[],
      Error,
      Page[],
      ReturnType<typeof getPagesByTypeListQueryKey>
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryKey: getPagesByTypeListQueryKey(sourceId, pageTypeList, objectType),
    queryFn: () =>
      PagesAPI.getPagesByTypeList(sourceId, pageTypeList, objectType),
    enabled: sourceId !== undefined,
    ...options,
  });
};

export const useSaveDocumentMutation = (
  documentId: number,
  options?: Omit<
    UseMutationOptions<void, Error, SaveDocumentPayload>,
    'queryKey' | 'queryFn'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, SaveDocumentPayload>({
    mutationFn: (payload) => {
      return PagesAPI.saveDocument(documentId, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: DOCUMENT_QUERY_KEY(documentId),
      });
    },
    ...options,
  });
};

export const useUpdateDocumentTitleMutation = (
  documentId: number,
  options?: Omit<
    UseMutationOptions<void, Error, string>,
    'queryKey' | 'queryFn'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (title) => PagesAPI.updateDocumentTitle(documentId, title),
    onSuccess: async () => {
      const queryKey = DOCUMENT_QUERY_KEY(documentId);

      queryClient.invalidateQueries({ queryKey });
    },
    ...options,
  });
};

export type DisablePageParams = {
  pageDocumentMappingId: number;
  pageType: PageType;
  documentId: number;
};

export const useDisablePageMutation = (
  options?: Omit<
    UseMutationOptions<void, Error, DisablePageParams>,
    'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, DisablePageParams>({
    mutationFn: ({ pageDocumentMappingId, pageType }) =>
      PagesAPI.deletePage(pageDocumentMappingId, pageType),
    onSuccess: async (_, variables) => {
      // Remove the document query cache first to prevent stale data from displaying
      queryClient.removeQueries({
        queryKey: DOCUMENT_QUERY_KEY(variables.documentId),
      });
      // Invalidate and AWAIT pages queries so the list is up-to-date
      // before the per-call onSuccess (which handles navigation) runs
      await queryClient.invalidateQueries({
        queryKey: ['pages'],
      });
    },
    ...options,
  });
};
